import {PersonBetter, RelationshipType, standardizeRelationshipType, toBackendRelationshipType, formatRelationshipTypeForDisplay} from "../types/Person";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_PATH_TO_API;

/**
 * Fetches a list of people associated with the current user.
 * Converts date of birth (dob) to Date objects and logs the result.
 */
export const fetchPeople = async (): Promise<PersonBetter[]> => {
    try {
        console.log('Fetching people from API...');
        const response = await axios.get(`${API_BASE_URL}/person`, {
            // params: {sub: localStorage.getItem('sub')},
            headers: {
                'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        });

        // Log the raw API response for debugging
        console.log('Raw API response data:', JSON.stringify(response.data, null, 2));
        console.log('Number of people returned:', response.data.length);
        
        // Check if any people have relationships
        const peopleWithRelationships = response.data.filter((p: any) => p.relationships && p.relationships.length > 0);
        console.log(`Found ${peopleWithRelationships.length} people with relationships`);
        
        if (peopleWithRelationships.length > 0) {
            console.log('Example person with relationships:', peopleWithRelationships[0]);
            console.log('Example relationships:', peopleWithRelationships[0].relationships);
        }

        const familyMembers: PersonBetter[] = response.data.map((member: any) => {
            // Create a timezone-neutral date object for DOB
            let normalizedDob = null;
            if (member.dob) {
                const dobObj = new Date(member.dob);
                normalizedDob = new Date(Date.UTC(
                    dobObj.getFullYear(),
                    dobObj.getMonth(),
                    dobObj.getDate(),
                    12, 0, 0, 0
                ));
            }
            
            // Process relationships if present
            console.log(`Checking relationships for ${member.first} ${member.last}:`, member.relationships);
            
            // Ensure relationships is at least an empty array
            let processedRelationships: any[] = [];
            
            try {
                // Check if relationships exists and is an array
                if (Array.isArray(member.relationships)) {
                    processedRelationships = member.relationships;
                    console.log(`Found ${processedRelationships.length} relationships for ${member.first} ${member.last}:`, processedRelationships);
                    
                    // Transform relationships to match frontend format
                    processedRelationships = processedRelationships.map((rel: any, index: number) => {
                        console.log(`Processing relationship ${index + 1}:`, rel);
                        
                        // Validate relationship data
                        if (!rel) {
                            console.warn('Relationship is null or undefined');
                            return null;
                        }
                        
                        if (!rel.type) {
                            console.warn('Relationship missing type:', rel);
                            return null;
                        }
                        
                        if (!rel.person) {
                            console.warn('Relationship missing person data:', rel);
                            return null;
                        }
                        
                        try {
                            // Use the standardization utility to convert to frontend format
                            const relationshipType = standardizeRelationshipType(rel.type);
                            console.log(`Standardized relationship type from ${rel.type} to ${relationshipType}`);
                            
                            // Check if person has required fields - handle both id and seed_key formats
                            const personId = rel.person.seed_key || rel.person.id;
                            if (!personId) {
                                console.warn('Person missing ID:', rel.person);
                            }
                            
                            if (!rel.person.first) {
                                console.warn('Person missing first name:', rel.person);
                            }
                            
                            if (!rel.person.last) {
                                console.warn('Person missing last name:', rel.person);
                            }
                            
                            // Create the relationship object with all available data
                            const processedRel = {
                                type: relationshipType,
                                custom_type: rel.custom_type,
                                name: personId || null,
                                person: rel.person ? {
                                    seed_key: personId,
                                    first: rel.person.first || '',
                                    middle: rel.person.middle || '',
                                    last: rel.person.last || '',
                                    dob: rel.person.dob ? new Date(rel.person.dob) : undefined
                                } : undefined
                            };
                            
                            console.log(`Successfully processed relationship:`, processedRel);
                            return processedRel;
                        } catch (error) {
                            console.error(`Error processing relationship ${index + 1}:`, error);
                            return null;
                        }
                    }).filter(Boolean); // Remove null entries
                    
                    // Log the final processed relationships
                    console.log(`Final processed relationships for ${member.first} ${member.last}:`, processedRelationships);
                } else {
                    console.warn(`Relationships for ${member.first} ${member.last} is not an array:`, member.relationships);
                }
            } catch (error) {
                console.error(`Error processing relationships for ${member.first} ${member.last}:`, error);
            }
            
            // Process life events if present
            let processedLifeEvents = member.life_events || [];
            if (processedLifeEvents.length > 0) {
                console.log(`Processing ${processedLifeEvents.length} life events for ${member.first} ${member.last}`);
                
                // Transform life events to match frontend format
                processedLifeEvents = processedLifeEvents.map((event: any) => {
                    return {
                        type: event.type,
                        date: event.date ? new Date(event.date) : null,
                        description: event.description || '',
                        isLocked: event.type === 'Birth' // Birth events are locked
                    };
                });
            }
            
            return {
                ...member,
                dob: normalizedDob,
                relationships: processedRelationships,
                life_events: processedLifeEvents
            };
        });

        console.log('Parsed family members with timezone-neutral dates:', familyMembers);

        return familyMembers;
    } catch (error) {
        console.error('Failed to fetch people:', error);
        throw new Error('Failed to load people. Please try again later.');
    }
};

/**
 * Creates a new person by splitting the provided name into first and last names.
 * Handles edge cases where the name is non-standard.
 * Makes a POST request to the /person endpoint and returns the created PersonBetter.
 */
export const createPerson = async (name: string, face_key: string): Promise<PersonBetter> => {
    try {
        // Basic name splitting logic
        const nameParts = name.trim().split(" ");

        let firstName = nameParts[0] || "";
        let lastName = nameParts.slice(1).join(" ") || "";

        const payload = {
            first: firstName,
            last: lastName,
            profile_photo_key: face_key
        };

        console.log(payload);

        const response = await axios.post(`${API_BASE_URL}/person`, payload, {
            headers: {
                Authorization: `Bearer ${sessionStorage.getItem("token")}`,
                "Content-Type": "application/json",
            },
        });

        // Create a timezone-neutral date object for DOB if it exists
        let normalizedDob;
        if (response.data.dob) {
            const dobObj = new Date(response.data.dob);
            normalizedDob = new Date(Date.UTC(
                dobObj.getFullYear(),
                dobObj.getMonth(),
                dobObj.getDate(),
                12, 0, 0, 0
            ));
        }

        const createdPerson: PersonBetter = {
            ...response.data,
            dob: normalizedDob
        };
        
        console.log("Created person:", createdPerson);
        return createdPerson;
    } catch (error) {
        console.error("Failed to create person:", error);
        throw new Error("Failed to create person. Please try again later.");
    }
};


interface UpdatePersonPayload {
    seed_key: string;
    first?: string;
    middle?: string;
    last?: string;
    dob?: string; // Notice this is a string because we're sending it as ISO format
    profile_image_key?: string;
    notes?: string;
    relationships?: Array<{
        type: string;
        person: {
            id: string;
            name?: string;
        };
        custom_type?: string;
    }>;
    life_events?: Array<{
        type: string;
        date: string | null;
        description?: string;
    }>;
}

export const updatePerson = async (person: Partial<PersonBetter> & { seed_key: string }): Promise<PersonBetter> => {
    try {
        console.log('API - updatePerson called with:', person);
        console.log('API - Environment variables:', {
            API_BASE_URL,
            token_exists: !!sessionStorage.getItem('token')
        });
        
        const payload: UpdatePersonPayload = {
            seed_key: person.seed_key,
            first: person.first,
            middle: person.middle,
            last: person.last,
            profile_image_key: person.profile_image_key,
            notes: person.notes? person.notes : undefined,
        };

        // Handle date of birth - set to noon UTC to avoid timezone issues
        if (person.dob) {
            const dobObj = new Date(person.dob);
            // Create a new date with only year, month, day at noon UTC
            const utcDob = new Date(Date.UTC(
                dobObj.getFullYear(),
                dobObj.getMonth(),
                dobObj.getDate(),
                12, 0, 0, 0
            ));
            payload.dob = utcDob.toISOString();
        }
        
        // Handle relationships if present
        if (person.relationships && person.relationships.length > 0) {
            console.log('API - Processing relationships for update:', person.relationships);
            
            // Filter out incomplete relationships (must have both type and person)
            const validRelationships = person.relationships.filter(rel => {
                const hasType = !!rel.type;
                const hasPerson = !!(rel.person?.seed_key || rel.name);
                const isValid = hasType && hasPerson;
                
                if (!isValid) {
                    console.log('API - Skipping incomplete relationship:', rel);
                }
                
                return isValid;
            });
            
            console.log(`API - Found ${validRelationships.length} valid relationships out of ${person.relationships.length}`);
            
            if (validRelationships.length > 0) {
                payload.relationships = validRelationships.map(rel => {
                    // Convert relationship type to backend format
                    const backendType = toBackendRelationshipType(rel.type);
                    console.log(`Converting relationship type from ${rel.type} to backend format: ${backendType}`);
                    
                    return {
                        type: backendType,
                        custom_type: rel.custom_type,
                        person: {
                            id: rel.person?.seed_key || rel.name,
                            name: rel.person ? `${rel.person.first} ${rel.person.last}`.trim() : ''
                        }
                    };
                });
            } else {
                console.log('API - No valid relationships to send');
            }
        }
        
        // Handle life events if present
        if (person.life_events && person.life_events.length > 0) {
            console.log('API - Processing life events for update:', person.life_events);
            
            // Filter out incomplete life events (must have both type and date)
            const validLifeEvents = person.life_events.filter(event => {
                const hasType = !!event.type;
                const hasDate = !!event.date;
                const isValid = hasType && hasDate;
                
                if (!isValid) {
                    console.log('API - Skipping incomplete life event:', event);
                }
                
                return isValid;
            });
            
            console.log(`API - Found ${validLifeEvents.length} valid life events out of ${person.life_events.length}`);
            
            if (validLifeEvents.length > 0) {
                payload.life_events = validLifeEvents.map(event => {
                    let formattedDate = null;
                    if (event.date) {
                        const dateObj = new Date(event.date);
                        // Create a timezone-neutral date
                        formattedDate = new Date(Date.UTC(
                            dateObj.getFullYear(),
                            dateObj.getMonth(),
                            dateObj.getDate(),
                            12, 0, 0, 0
                        )).toISOString();
                    }
                    
                    return {
                        type: event.type,
                        date: formattedDate,
                        description: event.description
                    };
                });
            } else {
                console.log('API - No valid life events to send');
            }
        }

        console.log('API - Sending update payload to API:', JSON.stringify(payload, null, 2));
        console.log('API - Request URL:', `${API_BASE_URL}/person`);
        
        const token = sessionStorage.getItem("token");
        if (!token) {
            console.error('API - No authentication token found in sessionStorage');
            throw new Error('Authentication token is missing');
        }
        
        console.log('API - Making axios PATCH request with token:', token.substring(0, 10) + '...');
        
        const response = await axios.patch(`${API_BASE_URL}/person`, payload, {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        });
        
        console.log('API response:', response.data);

        // When parsing the response, create a Date object but ensure it's timezone-neutral
        let updatedDob;
        if (response.data.dob) {
            const responseDobObj = new Date(response.data.dob);
            updatedDob = new Date(Date.UTC(
                responseDobObj.getFullYear(),
                responseDobObj.getMonth(),
                responseDobObj.getDate(),
                12, 0, 0, 0
            ));
        }

        // Process the complete person data returned from the API
        const responseData = response.data;
        console.log("API response data:", responseData);
        
        // Process relationships if present
        let processedRelationships = responseData.relationships || [];
        if (processedRelationships.length > 0) {
            console.log(`Processing ${processedRelationships.length} relationships from API response`);
            
            // Transform relationships to match frontend format
            processedRelationships = processedRelationships.map((rel: any) => {
                if (!rel.type || !rel.person) {
                    console.log('Skipping invalid relationship:', rel);
                    return null;
                }
                
                console.log('Processing relationship from update:', rel);
                
                // Use the standardization utility to convert to frontend format
                const relationshipType = standardizeRelationshipType(rel.type);
                
                console.log(`Standardized relationship type from ${rel.type} to ${relationshipType}`);
                
                return {
                    type: relationshipType,
                    custom_type: rel.custom_type,
                    name: rel.person?.id || null,
                    person: rel.person ? {
                        seed_key: rel.person.id,
                        first: rel.person.first,
                        middle: rel.person.middle,
                        last: rel.person.last,
                        dob: rel.person.dob ? new Date(rel.person.dob) : undefined
                    } : undefined
                };
            }).filter(Boolean); // Remove null entries
        }
        
        // Process life events if present
        let processedLifeEvents = responseData.life_events || [];
        if (processedLifeEvents.length > 0) {
            console.log(`Processing ${processedLifeEvents.length} life events from API response`);
            
            // Transform life events to match frontend format
            processedLifeEvents = processedLifeEvents.map((event: any) => {
                return {
                    type: event.type,
                    date: event.date ? new Date(event.date) : null,
                    description: event.description || '',
                    isLocked: event.type === 'Birth' // Birth events are locked
                };
            });
        }
        
        const updatedPerson: PersonBetter = {
            ...responseData,
            dob: updatedDob,
            relationships: processedRelationships,
            life_events: processedLifeEvents
        };

        console.log("Complete updated person with relationships and life events:", updatedPerson);
        return updatedPerson;
    } catch (error) {
        console.error("API - Failed to update person:", error);
        
        // Log more detailed error information
        if (axios.isAxiosError(error)) {
            console.error('API - Axios error details:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                config: {
                    url: error.config?.url,
                    method: error.config?.method,
                    headers: error.config?.headers
                }
            });
        }
        
        throw new Error("Failed to update person. Please try again later.");
    }
};